import {
  deletionMappingsReducer,
  getConversionTypeForColumn,
} from "./SicCodesTransformerSupport";
import CentredLoader from "components/inputs/ingestion/CentredLoader";
import DataValidation from "components/inputs/ingestion/DataValidation";
import { CLAIMS_MAPPING_IGNORE_VALUE, EXCLUDE_KEY } from "config";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as tablesService from "services/tablesService";
import * as hazardActions from "store/actions/input/exposure/hazardActions";

export const SicCodesTransformer = ({
  fileId,
  origin,
  tableName,
  headerRows,
  mapping,
  substitutions,
  rowDeletions,
  filename,
  tablesList,
  onConversionComplete,
  setDateConversionType,
  dateConversionTypeList,
  onSubstitutionsChanged,
  onRowDeletionsChanged,
}) => {
  const [columnMappings, setColumnMappings] = useState({});
  const [progressMessage, setProgressMessage] = useState("");
  const [deletionMappings, setDeletionMappings] = useState({});
  const [requestSubstitutions, setRequestSubstitutions] = useState([]);
  const [requestDeletions, setRequestDeletions] = useState([]);

  useEffect(() => {
    const colMap = {};
    if (mapping) {
      for (const index in mapping) {
        const entry = mapping[index];
        colMap[entry.outputName] = entry.inputColumn;
      }
      setColumnMappings(colMap);
    }
  }, [mapping, setColumnMappings]);

  const addDeletionMapping = useCallback(
    (key, deletions) => {
      const excludeColumn = columnMappings[key];
      if (!excludeColumn && excludeColumn !== 0) return;
      setDeletionMappings(deletionMappingsReducer(excludeColumn, deletions));
    },
    [columnMappings]
  );

  useEffect(() => {
    if (Object.keys(columnMappings ?? {}).length > 0) {
      if (EXCLUDE_KEY in columnMappings) {
        const excMapping = getConversionTypeForColumn(
          mapping,
          EXCLUDE_KEY,
          "IN_DELETE_ROW"
        );
        if (!excMapping) return;
        const excVals = Object.entries(excMapping?.conversions)
          .filter(([_, func]) => func === CLAIMS_MAPPING_IGNORE_VALUE)
          .map(([val, _]) => val);
        if (excVals.length > 0) {
          addDeletionMapping(EXCLUDE_KEY, excVals);
        }
      }
    }
  }, [
    columnMappings,
    setDeletionMappings,
    deletionMappings,
    mapping,
    addDeletionMapping,
  ]);

  const { data: headings } = tablesService.useGetHeaderRowQuery(
    {
      fileId,
      tableName,
      skipRows: origin?.row ?? 0,
      skipColumns: origin?.column ?? 0,
      headerRows,
    },
    { skip: !fileId || !tableName }
  );

  const {
    data: mappingTable,
    status: mappingTableStatus,
  } = tablesService.useGetMappingQuery(
    {
      mappingName: "sic_codes",
      fileId,
      columnMappings,
      substitutions: requestSubstitutions,
      rowDeletions: requestDeletions,
      deletionMappings: deletionMappings ? Object.values(deletionMappings) : [],
    },
    {
      skip: !fileId,
    }
  );

  const {
    data: conversionErrors,
    status: conversionErrorsStatus,
  } = tablesService.useGetConversionErrorsQuery(
    {
      fileId,
      tableName,
      skipColumns: origin?.column ?? 0,
      skipRows: origin?.row ?? 0,
      headerRows,
      skipBlankRows: true,
      mapping: mappingTable,
    },
    { skip: mappingTableStatus !== "fulfilled" || !fileId || !tableName }
  );

  const hasConversionErrors =
    conversionErrorsStatus === "fulfilled" && conversionErrors?.errors?.length;

  const {
    data: processingResponse,
    status: processingResponseStatus,
  } = tablesService.useDoConversionQuery(
    {
      fileId,
      tableName,
      skipColumns: origin?.column ?? 0,
      skipRows: origin?.row ?? 0,
      headerRows,
      skipBlankRows: true,
      mapping: mappingTable,
      progressNotification: (timing) => {
        if (timing?.rowsProcessed) {
          if (timing.percentageComplete === 100) {
            setProgressMessage("Process complete, Saving...");
          } else {
            setProgressMessage(
              `${timing.rowsProcessed} rows processed ` +
                `${timing.percentageComplete}% estimated ` +
                `${timing.remainingSeconds} seconds left`
            );
          }
        } else {
          setProgressMessage("initialising...");
        }
      },
    },
    {
      skip:
        mappingTableStatus !== "fulfilled" ||
        hasConversionErrors ||
        !fileId ||
        !tableName,
    }
  );

  const dispatch = useDispatch();
  const [createdFile, setCreatedFile] = useState(null);
  useEffect(() => {
    if (
      processingResponseStatus === "fulfilled" &&
      processingResponse.createdFile &&
      createdFile !== processingResponse.createdFile
    ) {
      setCreatedFile(processingResponse.createdFile);
      dispatch(hazardActions.addHazardFile(processingResponse.createdFile));
      onConversionComplete();
    }
  }, [
    createdFile,
    setCreatedFile,
    processingResponse,
    processingResponseStatus,
    dispatch,
    fileId,
    tableName,
    filename,
    tablesList,
    onConversionComplete,
  ]);

  const setRequestValues = () => {
    const activeSubstitutions = substitutions
      .filter((x) => !rowDeletions.includes(x.row))
      .reduce((acc, { column, row, value }) => {
        if (!acc[column]) {
          acc[column] = { column, replacements: [] };
        }
        acc[column].replacements.push({ row, value });
        return acc;
      }, {});

    setRequestSubstitutions(Object.values(activeSubstitutions));
    setRequestDeletions(rowDeletions);
  };

  if (
    processingResponseStatus === "rejected" ||
    conversionErrorsStatus === "rejected"
  ) {
    return <>{"Error - failed to load file"}</>;
  } else if (conversionErrorsStatus !== "fulfilled") {
    return <CentredLoader label={"Validating data " + progressMessage} />;
  } else if (hasConversionErrors) {
    return (
      <DataValidation
        conversionErrors={conversionErrors}
        headings={headings}
        origin={origin}
        substitutions={substitutions}
        rowDeletions={rowDeletions}
        setDateConversionType={setDateConversionType}
        dateConversionTypeList={dateConversionTypeList}
        onReProcess={setRequestValues}
        onSubstitutionsChanged={onSubstitutionsChanged}
        onRowDeletionsChanged={onRowDeletionsChanged}
      />
    );
  } else {
    return <CentredLoader label={"Validating data " + progressMessage} />;
  }
};

export default SicCodesTransformer;
